import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

const STORAGE_KEYS = {
  admin: 'adminAuth',
  client: 'clientAuth',
  endUser: 'userAuth',
};

// Maps API response to normalized user shape per role
const normalizeUser = {
  admin: (data) => ({
    id: data.id,
    name: data.name,
    username: data.email,
    role: data.role,
    email: data.email,
    permissions: data.role === 'Enterprise' ? 'all' : 'limited',
  }),
  client: (data) => ({
    id: data.id,
    name: data.name,
    email: data.email,
    role: data.role || 'sub_admin',
  }),
  endUser: (data) => ({
    id: data.id,
    name: data.name || data.username,
    email: data.email,
    username: data.username,
    plan: data.plan || 'basic',
    minutes_remaining: data.minutes_remaining || 0,
    minutes_used: data.minutes_used || 0,
    parent_id: data.parent_id,
  }),
};

// Auth API methods per role
const loginMethods = {
  admin: (creds) => authAPI.adminLogin(creds.username, creds.password),
  client: (creds) => authAPI.clientLogin(creds.email, creds.password),
  endUser: (creds) => creds.username
    ? authAPI.endUserLoginByUsername(creds.username, creds.password)
    : authAPI.endUserLogin(creds.email, creds.password),
};

function useRoleAuth(role) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const storageKey = STORAGE_KEYS[role];

  // Bootstrap: prefer httpOnly cookie via /auth/me, fall back to sessionStorage
  useEffect(() => {
    let cancelled = false;

    const expectedServerRole = role === 'endUser' ? 'end_user' : role;

    const hydrateFromStorage = () => {
      try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.user && !cancelled) {
            setIsAuthenticated(true);
            setUser(parsed.user);
          }
        }
      } catch {
        sessionStorage.removeItem(storageKey);
      }
    };

    const bootstrap = async () => {
      // First, try server-side session rehydration via httpOnly cookie
      try {
        const response = await authAPI.getCurrentUser();
        if (!cancelled && response.data?.success && response.data?.role === expectedServerRole) {
          const userData = normalizeUser[role](response.data.user);
          setIsAuthenticated(true);
          setUser(userData);
          sessionStorage.setItem(storageKey, JSON.stringify({ user: userData }));
          setLoading(false);
          return;
        }
      } catch {
        // No active session or wrong-role cookie — fall through to local storage
      }
      hydrateFromStorage();
      if (!cancelled) setLoading(false);
    };

    bootstrap();
    return () => { cancelled = true; };
  }, [storageKey, role]);

  const login = useCallback(async (credentials) => {
    try {
      const response = await loginMethods[role](credentials);
      if (response.data.success) {
        const userData = normalizeUser[role](response.data.user);
        setIsAuthenticated(true);
        setUser(userData);
        sessionStorage.setItem(storageKey, JSON.stringify({ user: userData }));
        return { success: true };
      }
      return { success: false, error: 'Invalid credentials' };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid credentials';
      return { success: false, error: errorMsg };
    }
  }, [role, storageKey]);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    sessionStorage.removeItem(storageKey);
    // Best-effort cookie clear server-side
    authAPI.logout().catch(() => undefined);
  }, [storageKey]);

  return { isAuthenticated, user, login, logout, loading };
}

export const AuthProvider = ({ children }) => {
  const admin = useRoleAuth('admin');
  const client = useRoleAuth('client');
  const endUserAuth = useRoleAuth('endUser');

  // Wrap admin login to match existing signature: login(usernameOrEmail, password)
  const adminLogin = useCallback(
    (usernameOrEmail, password) => admin.login({ username: usernameOrEmail, password }),
    [admin]
  );

  // Wrap client login: clientLogin(email, password)
  const clientLogin = useCallback(
    (email, password) => client.login({ email, password }),
    [client]
  );

  // Wrap end-user login: userLogin(email, password)
  const userLogin = useCallback(
    (email, password) => endUserAuth.login({ email, password }),
    [endUserAuth]
  );

  // Wrap end-user login by username: userLoginByUsername(username, password)
  const userLoginByUsername = useCallback(
    (username, password) => endUserAuth.login({ username, password }),
    [endUserAuth]
  );

  return (
    <AuthContext.Provider value={{
      // Admin auth
      isAuthenticated: admin.isAuthenticated,
      user: admin.user,
      login: adminLogin,
      logout: admin.logout,
      loading: admin.loading,
      // Client auth
      isClientAuthenticated: client.isAuthenticated,
      clientUser: client.user,
      clientLogin,
      clientLogout: client.logout,
      clientLoading: client.loading,
      // End User auth
      isUserAuthenticated: endUserAuth.isAuthenticated,
      endUser: endUserAuth.user,
      userLogin,
      userLoginByUsername,
      userLogout: endUserAuth.logout,
      userLoading: endUserAuth.loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
